import { ClassValue } from 'clsx';
import { Icon } from '@shared/components/Icon';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { TodoField } from '@shared/data/entity/fields/TodoField';
import { i18n } from '@shared/util/i18n';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import { getTodoText } from './util/getTodoText';
import styles from './TodoCollectionViewField.module.css';

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: TodoField;
}

/**
 * Default: a summary of the progress. Compact: the to-do icon and "2 of 4". Inline: the
 * same, in a line of text.
 */
export function TodoCollectionViewField({ field, variant, className }: Props) {
  if (field.isEmpty) {
    return null;
  }

  const isInline = variant === 'inline';
  const isCompact = variant === 'compact';
  const isDefault = variant === 'default';

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={[styles.todoCollectionViewField, className]}
    >
      {isInline && <InlineTodo field={field} />}
      {isCompact && <CompactTodo field={field} />}
      {isDefault && <DefaultTodo field={field} />}
    </CollectionViewFieldContainer>
  );
}

function InlineTodo({ field }: { field: TodoField }) {
  return (
    <>
      <Icon name="check-todo" gridSize={2.5} className={styles.inlineIcon} />
      {getTodoText(field)}
    </>
  );
}

function CompactTodo({ field }: { field: TodoField }) {
  return (
    <View row centerVertically className={styles.line}>
      <Icon name="check-todo" gridSize={3.5} className={styles.icon} />
      <Text numberOfLines={1}>{getTodoText(field)}</Text>
    </View>
  );
}

function DefaultTodo({ field }: { field: TodoField }) {
  return (
    <Text size="body1">
      {i18n('card.blocks.todo.summary', {
        completedCount: field.todos.filter(item => item.status === 'COMPLETED').length,
        count: field.todos.length,
      })}
    </Text>
  );
}
